import { inject, Injectable } from "@angular/core";
import { FirebaseApp } from "@angular/fire/app";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  getFirestore,
  query,
  QueryConstraint,
  setDoc,
  updateDoc,
} from "@angular/fire/firestore";
import { FIRESTORE_BASE_PATH } from "@config";
import { AnalyticsService } from "@services/analytics.service";
import { ErrorBoundaryService } from "@services/error-boundary.service";

export type UsersCollectionId = "users";
export type UserDataCollectionId = "user_data";
export type SequenceResultsCollectionId = "sequence_results";
export type CollectionId = UsersCollectionId | UserDataCollectionId | SequenceResultsCollectionId;
@Injectable({
  providedIn: "root",
})
export class FirestoreClient {
  private firebaseApp = inject(FirebaseApp);
  private analyticsService = inject(AnalyticsService);
  private errorBoundaryService = inject(ErrorBoundaryService);
  private firestore = getFirestore(this.firebaseApp);

  async create(data: Record<string, unknown>, collectionId: CollectionId, docId?: string): Promise<string | undefined> {
    const collectionPath = collection(this.firestore, this.getCollectionPath(collectionId));
    let createdDocId: string | undefined;

    try {
      if (docId) {
        const newDocRef = doc(collectionPath, docId);
        await setDoc(newDocRef, data, { merge: true });
        createdDocId = docId;
      } else {
        const newDocRef = await addDoc(collectionPath, data);
        createdDocId = newDocRef.id;
      }
      return createdDocId;
    } catch (error) {
      this.analyticsService.trackException(error as Error);
      this.errorBoundaryService.triggerError();
      return undefined;
    }
  }

  async read<T>(collectionId: CollectionId, docId: string): Promise<T | undefined> {
    const collectionPath = collection(this.firestore, this.getCollectionPath(collectionId));
    const docRef = doc(collectionPath, docId) as DocumentReference<T>;

    try {
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        return;
      }
    } catch (error) {
      this.analyticsService.trackException(error as Error);
      this.errorBoundaryService.triggerError();
      return;
    }
  }

  async update(dataToUpdate: Record<string, unknown>, collectionId: CollectionId, docId: string) {
    const collectionPath = collection(this.firestore, this.getCollectionPath(collectionId));
    const docRef = doc(collectionPath, docId);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await updateDoc(docRef, dataToUpdate as Record<string, any>);
    } catch (error) {
      this.analyticsService.trackException(error as Error);
      this.errorBoundaryService.triggerError();
    }
  }

  async delete(collectionId: CollectionId, docId: string) {
    const collectionPath = collection(this.firestore, this.getCollectionPath(collectionId));
    const docRef = doc(collectionPath, docId);

    try {
      await deleteDoc(docRef);
    } catch (error) {
      this.analyticsService.trackException(error as Error);
      this.errorBoundaryService.triggerError();
    }
  }

  async query<T>(collectionId: CollectionId, constraints: QueryConstraint[] = []): Promise<T[] | undefined> {
    const collectionPath = this.getCollectionPath(collectionId);
    const colRef = collection(this.firestore, collectionPath);
    const q = query(colRef, ...constraints);

    try {
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as T);
      } else {
        return [];
      }
    } catch (error) {
      this.analyticsService.trackException(error as Error);
      this.errorBoundaryService.triggerError();
      return;
    }
  }

  private getCollectionPath(subcollectionId: CollectionId): string {
    if (subcollectionId === "users") {
      return "users";
    }

    return `${FIRESTORE_BASE_PATH}/${subcollectionId}`;
  }
}
